import React from 'react';
import { Heart, WarningCircle } from '@phosphor-icons/react';

import { MarketShell } from '../../design-system/components/shells';
import { Button, Empty, PageHeader, Pagination, SkeletonCard } from '../../design-system/components/desktop';
import { ListingCard, ListingGrid } from '../../design-system/patterns/market';
import { ModalNote } from '../../design-system/patterns/modal-form';
import { useViewport } from '../../design-system/hooks/useViewport';
import { formatCurrency, mediaUrl } from '../../utils/common';
import { errorText, LOAD_FAILURE } from '../../utils/errorText';
import { routes } from '../../routes/routes';

import './collection.css';

/**
 * The buyer's collection, the whole page. Pure.
 *
 * ONE WORD FOR THIS THING, AND IT IS "COLLECTION". The app had three — the
 * header icon said Wishlist, the dashboard tab said Collection, the profile
 * panel said saved cards — for one list held in one endpoint. Every label a
 * buyer reads now says Collection, which is the word the empty states, the
 * dashboard tab and the `collection_viewed` event already used. The directory
 * is still `Wishlist/` and the query key is still `saved-cards` because the
 * data layer's names are not user-facing and renaming them would touch the
 * dashboard, the card detail and the fixtures for no visible gain.
 *
 * A GRID, WHERE THE DASHBOARD TAB IS A TABLE, and the difference is deliberate.
 * `BuyerDashboard/SavedTable` is a management view sitting inside a console of
 * other tables: you go there to audit what a collection is worth. This is a
 * BUYING surface reached from the header heart mid-browse, so it shows the same
 * cards the marketplace shows, as cards. Both read the same rows from the same
 * `getSavedCards`, so they can never disagree about what is in the collection.
 *
 * THE CARD IS THE SHARED `ListingCard`, not a fifth implementation of it, for
 * the reason spelled out in `patterns/market`. Its heart is the ONLY control on
 * the tile: on this page every card is already saved, so the heart starts
 * filled and pressing it removes. That is why this renders `ListingGrid`
 * directly rather than reusing `MarketplaceGrid` — the browse grid does not
 * pass the wishlist props through, and a collection page whose only action is
 * "leave" would be worse than one card component too many.
 */

/** Map one favourites row onto the shared card's prop API. */
function listingProps(row) {
  return {
    image: row?.image_url ? mediaUrl(row.image_url) : undefined,
    imageAlt: row?.product_name || row?.name || '',
    /* The favourites payload is not consistent about these two names across
       the app's own consumers — `SavedTable` reads `set_name`, the preview
       fixture sends `catalog_group` — so both are accepted rather than picking
       one and rendering a blank kicker wherever the other is what arrived. */
    set: row?.catalog_group || row?.set_name || undefined,
    name: row?.product_name || row?.name || 'Unnamed card',
    rarity: row?.rarity || undefined,
    cardNumber: row?.card_number || undefined,
    /* Market value is the only price a favourites row carries, and it is
       Rands. It is labelled, because an unlabelled figure on a card reads as
       an asking price and this is not one — nothing here is for sale by the
       person looking at it. */
    price: row?.market_price != null ? formatCurrency(row.market_price) : undefined,
    fromLabel: row?.market_price != null ? 'Market' : undefined,
  };
}

export function CollectionView({
  rows = [],
  loading = false,
  error = null,
  onRetry,

  page = 1,
  pageCount = 1,
  pageSize = 24,
  total = 0,
  onPageChange,

  onOpenCard,
  onRemove,
  isGuest = false,
}) {
  const { isDesktop } = useViewport();

  let body;

  if (loading) {
    body = (
      <ListingGrid>
        {Array.from({ length: 12 }).map((_, index) => (
          <SkeletonCard key={index} />
        ))}
      </ListingGrid>
    );
  } else if (error) {
    body = (
      <Empty
        tone="error"
        icon={WarningCircle}
        illustration="request-failed"
        title="We could not load your collection"
        text={errorText(error, LOAD_FAILURE)}
        actions={
          onRetry ? (
            <Button variant="ghost" onClick={onRetry}>
              Try again
            </Button>
          ) : null
        }
      />
    );
  } else if (!rows.length) {
    body = (
      /* WORD FOR WORD what `SavedTable` and the profile panel say. Three
         surfaces render this same empty collection and they used to say three
         different things, which read as three different features. */
      <Empty
        icon={Heart}
        illustration="no-saved-items-yet"
        title="Your collection is empty"
        text="Tap the heart on any card and it will be waiting for you here."
        actions={
          <Button as="a" href={routes.MARKETPLACE} variant="ghost">
            Browse the marketplace
          </Button>
        }
      />
    );
  } else {
    body = (
      <ListingGrid>
        {rows.map((row) => (
          <ListingCard
            key={row.card_id || row.id}
            mode="tile"
            href={`${routes.MARKETPLACE}/card/${row.product}`}
            onClick={(event) => {
              if (!onOpenCard) return;
              /* `href` is what makes the tile a real link, so middle-click and
                 open-in-new-tab work; defaulting the event is what keeps a
                 plain click an in-app navigation on BOTH tracks. Same handling
                 as the browse grid. */
              event.preventDefault();
              onOpenCard(row);
            }}
            wishlist
            wishlisted
            wishlistLabel="Remove from collection"
            onWishlistToggle={() => onRemove && onRemove(row)}
            {...listingProps(row)}
          />
        ))}
      </ListingGrid>
    );
  }

  return (
    <MarketShell
      /* The page's own geometry: a wider cap than `.dsk-page`'s 1280 and a
         column count that steps with it, so the tiles stop growing past the
         size the card was drawn at. All of it, and the arithmetic, is in
         `collection.css`. Passed on both tracks — below the handoff this class
         lands on `.ds-appshell__main`, where neither rule can match. */
      contentClassName="dsa-collection"
      /* Still no `activeNav`, and now for a different reason. It used to be
         that the collection was not one of the header destinations, so
         lighting one up would say the buyer was somewhere they are not. It IS
         a tab-bar destination now — but `TabBar` falls back to matching the
         pathname when the shell hands it no `activeId`, and `/collection` is
         the tab's own `match`, so the tab lights itself. Passing an id here
         would only be a second, hand-maintained copy of that answer. */
      wishlistCount={total}
      /* The trail is the DESKTOP way back; below the handoff the mobile top bar
         titles itself from the trail, which would print "Collection" in the bar
         and again in the `<h1>` two lines under it. Same split as Games. */
      breadcrumbs={
        isDesktop ? [{ label: 'Marketplace', to: routes.MARKETPLACE }, { label: 'Collection' }] : []
      }
      back={{ to: routes.MARKETPLACE, label: 'Back to marketplace' }}
    >
      <PageHeader
        title="Collection"
        /* No count while it is loading: a subtitle that says "0 cards" and then
           corrects itself to 38 has told the buyer their collection is empty. */
        sub={
          loading || error
            ? undefined
            : `${Number(total).toLocaleString('en-ZA')} ${total === 1 ? 'card' : 'cards'} saved`
        }
      />

      {/* Only when there is something to keep: a prompt to sign in and keep
          nothing is nonsense, so this never shows above the empty state. */}
      {isGuest && rows.length > 0 ? (
        <ModalNote className="dsa-collection__guest-note">
          These are saved on this device. Sign in to keep them.
        </ModalNote>
      ) : null}

      {/* Skeleton cards are decorative and already hidden, so the busy state is
          announced on the region that is loading and not card by card. */}
      <div aria-busy={loading || undefined}>{body}</div>

      {!loading && !error && pageCount > 1 && (
        <Pagination
          /* Vendored as table chrome, mounted here as a page band, exactly as
             the browse grid mounts it. `.dsa-browse-pager` drops the table's
             24px inset and gives the grid air above the rule. */
          className="dsa-browse-pager"
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          totalItems={total}
          itemNoun="cards"
          onPageChange={onPageChange}
        />
      )}
    </MarketShell>
  );
}

export default CollectionView;
